import { type PostApplyStep } from '@components/app/ApplySuccessPage/PostApplyPrompt/stepTypes'
import { RoutePrefix } from '.'

const PatientRoutes = {
  applyCallAttribution: (patientTrialApplicationIdToken: string) =>
    `${RoutePrefix.PATIENTS}/apply-call/${patientTrialApplicationIdToken}`,
  applyEligibility: (patientTrialApplicationIdToken: string) =>
    `${RoutePrefix.PATIENTS}/apply-eligibility/${patientTrialApplicationIdToken}`,
  applyPackage: (patientTrialApplicationIdToken: string) =>
    `${RoutePrefix.PATIENTS}/apply-package/${patientTrialApplicationIdToken}`,
  applyReview: (patientTrialApplicationIdToken: string) =>
    `${RoutePrefix.PATIENTS}/apply-review/${patientTrialApplicationIdToken}`,
  bestMatch: `${RoutePrefix.PATIENTS}/best-match`,
  experimentalApplyFlow: {
    areCurrentSubstancesWillingToStop: (
      slug: string,
      prefix: string = 'apply3',
    ) =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/are-current-substances-willing-to-stop`,
    conditionsAreMedicationsHelping: (
      slug: string,
      prefix: string = 'apply3',
    ) =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/are-current-medications-helping`,
    conditionsAreMedicationsStableDose: (
      slug: string,
      prefix: string = 'apply3',
    ) =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/are-current-medications-stable-dose`,
    conditionsAreMedicationsWillingToStop: (
      slug: string,
      prefix: string = 'apply3',
    ) =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/are-current-medications-willing-to-stop`,
    conditionsComorbidities: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/comorbidities`,
    conditionsCurrentMedications: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/current-medications`,
    conditionsCurrentSubstances: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/current-substances`,
    currentSubstanceFrequency: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/conditions/current-substance-frequency`,
    demographicsConditions: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/conditions`,
    demographicsDateOfBirth: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/date-of-birth`,
    demographicsEmail: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/email`,
    demographicsEthnicIdentities: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/ethnic-identities`,
    demographicsGenderIdentities: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/gender-identities`,
    demographicsHeightInCm: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/height-in-cm`,
    demographicsName: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/name`,
    demographicsPhoneNumber: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/phone-number`,
    demographicsWeightInKg: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/weight-in-kg`,
    demographicsZipcode: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/demographics/zipcode`,
    englishSpeaking: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/english`,
    legalAge: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/legal-age`,
    locations: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/locations`,
    prescreener: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/prescreener`,
    shareTrial: (slug: string, prefix: string = 'apply3') =>
      `${RoutePrefix.PATIENTS}/${prefix}/${slug}/share-trial`,
  },
  inbox: `${RoutePrefix.PATIENTS}/inbox`,
  oauthCallback: `${RoutePrefix.PATIENTS}/oauth-callback`,
  pastApplications: `${RoutePrefix.PATIENTS}/past-applications`,
  patientReferral: (slug: string) =>
    `${RoutePrefix.PATIENTS}/referral/${slug.toLowerCase()}`,
  postApplyStep: (
    patientTrialApplicationIdToken: string,
    step: PostApplyStep,
  ) =>
    `${RoutePrefix.PATIENTS}/apply-success/${patientTrialApplicationIdToken}/${step}`, // also called post apply flow
  profile: `${RoutePrefix.PATIENTS}/profile`,
  signupFlow: {
    baseRoute: (slug: string, segmentAnonymousId: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousId}`,
    conditionsRoute: (slug: string, segmentAnonymousId: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousId}/conditions`,
    conditionsStep: (slug: string, segmentAnonymousId: string, step: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousId}/conditions/${step}`,
    demographicsRoute: (slug: string, segmentAnonymousId: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousId}/demographics`,
    demographicsStep: (
      slug: string,
      segmentAnonymousId: string,
      step: string,
    ) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousId}/demographics/${step}`,
    emailStep: (slug: string, segmentAnonymousId: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousId}/profile/email`,
    locationsStep: (slug: string, segmentAnonymousId: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousId}/locations`,
    prescreenerRoute: (slug: string, idToken: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${idToken}/prescreener`,
    profileRoute: (slug: string, segmentAnonymousIdOrIdToken: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${segmentAnonymousIdOrIdToken}/profile`,
    profileStep: (slug: string, idToken: string, step: string) =>
      `${RoutePrefix.PATIENTS}/signup/${slug}/${idToken}/profile/${step}`,
    signupSplit: (slug: string) => `${RoutePrefix.PATIENTS}/signup/${slug}`,
  },
  submitSourceApplyRoute: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/success`, // This is a *route* that calls persist complete trial apply
  trialApplyBegin: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/begin`, // v2 apply flow - step 0 (new variant; old variant is trialApplyStartOld)
  trialApplyBeginGeneric: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/begin/generic`, // v2 apply flow - step 0 (new generic variant)
  trialApplyEmail: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/email`, // v2 apply flow - step 1
  trialApplyEmailCheck: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/email-check`, // v2 apply flow - step 2
  trialApplyEnglishSpeaking: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/english`, // v2 apply flow - step 1 variant 1 (variant 2 is whyParticipate)
  trialApplyLegalAge: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/legal-age`, // v2 apply flow - step 1 - temporary to try and reduce email bounce rate
  trialApplyPatient: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/patient`, // v2 apply flow - step 3
  trialApplyPreferredApplyPackage: (patientTrialApplicationIdToken: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${patientTrialApplicationIdToken}/preferred-apply-package`, // v2 apply flow - route the user to the preferred apply package
  trialApplyRecommendations: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-recommendations/${token}`, // v2 apply flow - step 12
  trialApplyStart: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/apply`, // v2 apply flow - step -1 (do this so that all traffic is sent to the eppo experiment; redirects to trialApplyBegin or trialApplyStartOld)
  trialApplyStartOld: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/start`, // v2 apply flow - step 0 (old variant; new variant is trialApplyBegin)
  trialApplySurveyConditions: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/conditions`, // v2 apply flow - step 6
  trialApplySurveyConditionsStep: (token: string, step: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/conditions/${step}`, // v2 apply flow - step 6
  trialApplySurveyDemographics: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/demographics`, // v2 apply flow - step 4
  trialApplySurveyDemographicsStep: (token: string, step: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/demographics/${step}`, // v2 apply flow - step 4
  trialApplySurveyLocations: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/locations`, // v2 apply flow - step 5
  trialApplySurveyLocationsExperiment: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/locations-experiment`, // v2 apply flow - step 5 (experiment router)
  trialApplySurveyMultiTrialApply: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/multi-trial-apply`, // v2 apply flow - multi-trial step
  trialApplySurveyPostscreener: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/postscreener`, // v2 apply flow - step 10
  trialApplySurveyPostscreenerCriterion: (token: string, criterionId: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/postscreener/${criterionId}`, // v2 apply flow - step 11
  trialApplySurveyPrescreener: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/prescreener`, // v2 apply flow - step 7
  trialApplySurveyPrescreenerCriterion: (token: string, criterionId: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/prescreener/${criterionId}`, // v2 apply flow - step 8
  trialApplySurveyPrescreenerVerifier: (token: string, slug: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/prescreener/verifiers/${slug}`, // v2 apply flow - step 8.5
  trialApplySurveyPrescreenerVerifierStep: (
    token: string,
    slug: string,
    step: string,
  ) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/prescreener/verifiers/${slug}/${step}`, // v2 apply flow - step 8.75
  trialApplySurveyTrialLocations: (token: string) =>
    `${RoutePrefix.PATIENTS}/apply-survey/${token}/trial-locations`, // v2 apply flow - step 5 (trial locations experiment)
  trialApplyWhyParticipate: (slug: string) =>
    `${RoutePrefix.PATIENTS}/trial-apply/${slug}/why-participate`, // v2 apply flow - step 1 variant 2 (variant 1 is english)
}

export default PatientRoutes
